import { useState, useEffect, useMemo, useRef } from "react";
import Input from "../ui/Input";
import Button from "../ui/Button";
import { useUpdatePayroll } from "../Use-auth";
import { Pencil, Eye, X, HandCoins } from "lucide-react";
import Pagination from "./Pagination";
import SkeletonPage from "./SkeletonPage ";

const formatAmount = (amount) => {
    if (!amount) return "0";
    return Math.round(amount).toLocaleString();
};

const InfoRow = ({ label, value, className }) => (
    <tr className="text-[10px] sm:text-[12px]">
        <td className="px-4 py-2 font-medium text-gray-600 w-1/3">
            {label}
        </td>

        <td className={`px-4 py-2 text-gray-800 ${className} inline-block `}>
            {value || "-"}
        </td>
    </tr >
);

const getPreviousMonth = () => {
    const now = new Date();
    now.setMonth(now.getMonth() - 1);
    return now.toISOString().slice(0, 7);
};

export default function PayRollHistry({ employeeId, salary = [], username, workEmail, personalEmail, employeeStatus }) {
    const [showForm, setShowForm] = useState(false);
    const [openSelectIndex, setOpenSelectIndex] = useState(null);
    const { mutate: updateUser, isPending: isLoading } = useUpdatePayroll();
    const [editIndex, setEditIndex] = useState(null);
    const [isEdit, setIsEdit] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [tempStatus, setTempStatus] = useState("");
    const [localSalary, setLocalSalary] = useState(salary);
    const selectRef = useRef(null);
    const [loadingRowIndex, setLoadingRowIndex] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const formsPerPage = 10;
    const isPageLoading = salary === undefined || salary === null;
    const [selectedDate, setSelectedDate] = useState(getPreviousMonth());

    useEffect(() => {
        if (Array.isArray(salary)) {
            setLocalSalary(salary);
        }
    }, [salary]);

    const totalPages = Math.ceil(
        localSalary.filter((item) => Number(item.netSalary) !== 0).length / formsPerPage
    );

    const paginatedSalary = useMemo(() => {
        const start = (currentPage - 1) * formsPerPage;
        const end = currentPage * formsPerPage;
        return localSalary.slice(start, end);
    }, [localSalary, currentPage]);

    const handlePageChange = (page) => setCurrentPage(page);

    const [form, setForm] = useState({
        basicSalary: '',
        nightShift: '',
        bonus: '',
        increment: '',
        loan: '',
        leave: '',
        other: '',
    });

    const handleChange = (field, value) => {
        setForm({
            ...form,
            [field]: Number(value) || 0,
        });
    };

    const totalEarnings = useMemo(
        () => form.basicSalary + form.nightShift + form.bonus + form.increment,
        [form]
    );

    const totalDeductions = useMemo(
        () => form.loan + form.leave + form.other,
        [form]
    );

    const netSalary = totalEarnings - totalDeductions;

    const latestCtc = salary
        .find((s) => s.annualCtc > 0)?.annualCtc || 0;

    useEffect(() => {
        if (salary.length > 0 && showForm && !isEdit) {
            const latest = salary.find((item) => item.annualCtc > 0);

            if (latest?.annualCtc) {
                const monthly = Math.round(latest.annualCtc / 12);

                setForm((prev) => ({
                    ...prev,
                    basicSalary: monthly,
                }));
            }
        }
    }, [salary, showForm, isEdit]);

    const totalSalary = localSalary.reduce(
        (sum, emp) => sum + Number(emp.netSalary),
        0
    );

    const handleSave = () => {
        if (!employeeId) return alert("Missing employee id");
        if (totalEarnings <= 0) return alert("Enter salary first");
        const now = new Date();
        const payrollItem = {
            id: isEdit && editIndex !== null ? localSalary[editIndex].id : undefined,
            userId: employeeId,
            username: username,
            email: [workEmail, personalEmail],
            annualCtc: form.annualCtc || latestCtc,
            monthlyCreated: selectedDate ? new Date(selectedDate) : new Date(),
            earnings: {
                basicSalary: form.basicSalary,
                nightShiftAllowance: form.nightShift,
                performanceBonus: form.bonus,
                incrementAmount: form.increment,
                totalEarnings,
            },
            deductions: {
                loanAdvance: form.loan,
                leaveDeduction: form.leave,
                otherDeduction: form.other,
                totalDeductions,
            },
            netSalary,
            status: isEdit && editIndex !== null
                ? localSalary[editIndex].status
                : "Pending",
            createdAt: isEdit && editIndex !== null ? localSalary[editIndex].createdAt : new Date(),
            updatedAt: new Date(),
        };

        let updatedPayroll = [...localSalary];

        if (isEdit && editIndex !== null) {
            updatedPayroll[editIndex] = payrollItem;
        } else {
            const exists = localSalary.some(p =>
                p.userId === payrollItem.userId &&
                p.annualCtc === payrollItem.annualCtc &&
                p.netSalary === payrollItem.netSalary &&
                p.earnings.totalEarnings === payrollItem.earnings.totalEarnings &&
                p.deductions.totalDeductions === payrollItem.deductions.totalDeductions
            );

            if (exists) return alert("This payroll entry already exists!");
            updatedPayroll.push(payrollItem);
        }

        updateUser({ employeeId, payroll: updatedPayroll }, {
            onSuccess: () => {
                setLocalSalary(updatedPayroll);
                resetForm();
            }
        });
    };

    const resetForm = () => {
        setShowForm(false);
        setIsEdit(false);
        setEditIndex(null);
        setForm({
            basicSalary: 0,
            nightShift: 0,
            bonus: 0,
            increment: 0,
            loan: 0,
            leave: 0,
            other: 0,
            annualCtc: 0,
        });
    };

    const handleEdit = (item, index) => {
        const basic = Number(item?.earnings?.basicSalary) || 0;

        setForm((prev) => ({
            ...prev,
            basicSalary: basic,
            nightShift: Number(item?.earnings?.nightShiftAllowance) || 0,
            bonus: Number(item?.earnings?.performanceBonus) || 0,
            increment: Number(item?.earnings?.incrementAmount) || 0,
            loan: Number(item?.deductions?.loanAdvance) || 0,
            leave: Number(item?.deductions?.leaveDeduction) || 0,
            other: Number(item?.deductions?.otherDeduction) || 0,
        }));

        setEditIndex(index);
        setIsEdit(true);
        setShowForm(true);
    };

    const statusSalery = ["Pending", "Hold", "Paid"];
    const statusColor = {
        Paid: "text-green-600",
        Pending: "text-yellow-600",
        Hold: "text-red-600",
    };

    const handleStatusChange = (index, status) => {
        if (!localSalary[index]?.id) return;

        setLoadingRowIndex(index);

        const updatedPayroll = [...localSalary];
        updatedPayroll[index] = {
            ...updatedPayroll[index],
            status,
            email: [workEmail, personalEmail],
            userId: employeeId,
            username: username,
            updatedAt: new Date(),
        };

        setLocalSalary(updatedPayroll);

        updateUser({ employeeId, payroll: updatedPayroll }, {
            onSuccess: () => {
                setLoadingRowIndex(null);
                setOpenSelectIndex(null);
            },
            onError: () => {
                setLoadingRowIndex(null);
                alert("Failed to update status");
            }
        });
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (selectRef.current && !selectRef.current.contains(event.target)) {
                setOpenSelectIndex(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    if (isPageLoading && !showForm) {
        return (
            <div className="mt-6 p-2 sm:p-4 border rounded bg-white">
                <SkeletonPage />
            </div>
        );
    }

    return (
        <div>
            <div className="mt-6 p-2 sm:p-4 border rounded bg-white">

                <div className="flex justify-between items-center mb-3">
                    <h2 className="text-[14px] sm:text-[20px] font-bold">Payroll History</h2>

                    <Button
                        onClick={() => {
                            if (showForm) {
                                setShowForm(false);
                                setIsEdit(false);
                                setEditIndex(null);
                            } else {
                                setForm({
                                    basicSalary: latestCtc ? Math.round(latestCtc / 12) : 0,
                                    nightShift: '',
                                    bonus: '',
                                    increment: '',
                                    loan: '',
                                    leave: '',
                                    other: '',
                                });
                                setShowForm(true);
                            }
                        }}
                    >
                        {showForm ? "Close" : "Add Salary"}
                    </Button>
                </div>

                {showForm && (
                    <div className="border p-3 rounded bg-gray-50 mb-4">

                        <h3 className="font-semibold mb-2">Earnings</h3>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-2">
                            <div className="flex-1 text-[11px] sm:text-sm">Select Month</div>

                            <div className="w-60">
                                <input
                                    type="month"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    className="w-full border rounded px-2 py-1"
                                />
                            </div>
                        </div>
                        <Row
                            label={employeeStatus === "Trainee" ? "Training Stipend" : "Monthly Salary"}
                            value={form.basicSalary}
                            disabled={!isEdit && !!latestCtc}
                            onChange={(v) => handleChange("basicSalary", v)}
                        />
                        <Row label="Night Shift" value={form.nightShift}
                            onChange={(v) => handleChange("nightShift", v)} />

                        <Row label="Bonus" value={form.bonus}
                            onChange={(v) => handleChange("bonus", v)} />

                        <Row label="Increment" value={form.increment}
                            onChange={(v) => handleChange("increment", v)} />

                        <h3 className="font-semibold mt-4 mb-2 border-t pt-2">Deductions</h3>

                        <Row label="Loan" value={form.loan}
                            onChange={(v) => handleChange("loan", v)} />

                        <Row label="Leave" value={form.leave}
                            onChange={(v) => handleChange("leave", v)} />

                        <Row label="Other" value={form.other}
                            onChange={(v) => handleChange("other", v)} />

                        <div className="mt-3 text-sm bg-white p-2 rounded border">
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-[12px] sm:text-[16px]">Total Earnings:</span>
                                    <span className="text-[12px] sm:text-[16px]">₹ {formatAmount(totalEarnings)}</span>
                                </div>

                                <div className="flex justify-between">
                                    <span className="text-[12px] sm:text-[16px]">Total Deductions:</span>
                                    <span className="text-[12px] sm:text-[16px]">₹ {formatAmount(totalDeductions)}</span>
                                </div>

                                <div className="flex justify-between font-semibold text-green-700">
                                    <span className="text-[12px] sm:text-[16px]">Net Salary:</span>
                                    <span className="text-[12px] sm:text-[16px]">₹ {formatAmount(netSalary)}</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end mt-3">
                            <Button
                                onClick={handleSave}
                                disabled={isLoading}
                                variant="success"
                                className="min-w-[120px]"
                            >
                                {isLoading ? (
                                    <div className="flex items-center gap-2 justify-center">
                                        Saving...
                                        <span className="loader-circle"></span>

                                    </div>
                                ) : (
                                    isEdit ? "Update" : "Save"
                                )}
                            </Button>
                        </div>
                    </div>
                )}

                {localSalary.length === 0 && (
                    <p className="text-gray-400 text-[11px] sm:text-sm">
                        No payroll records
                    </p>
                )}

                {localSalary.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="w-full border text-[11px] sm:text-sm">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="border p-1 rs:p-2 text-[11px] sm:text-sm ">Salary Month</th>
                                    {/* <th className="border p-1 rs:p-2">CTC</th> */}
                                    <th className="border p-1 rs:p-2 text-[11px] sm:text-sm hidden custom:table-cell">Earnings</th>
                                    <th className="border p-1 rs:p-2 text-[11px] sm:text-sm hidden custom:table-cell">Deductions</th>
                                    <th className="border p-1 rs:p-2 text-[11px] sm:text-sm hidden custom:table-cell">Net Salary</th>
                                    <th className="border p-1 rs:p-2 text-[11px] sm:text-sm">Status</th>
                                    <th className="border p-1 rs:p-2 text-[11px] sm:text-sm">Paid Date</th>
                                    <th className="border p-1 rs:p-2 text-[11px] sm:text-sm">Actions</th>
                                </tr>
                            </thead>

                            <tbody>
                                {/* {isLoading && (
                                    <tr>
                                        <td colSpan={7} className="p-6">
                                            <SkeletonPage />
                                        </td>
                                    </tr>
                                )} */}

                                {paginatedSalary.map((item, idx) => {
                                    if (Number(item.netSalary) === 0) return null;
                                    const actualIndex = localSalary.findIndex(
                                        s => s.id === item.id);

                                    return (
                                        <tr key={idx} className="text-center text-[11px] sm:text-sm">

                                            <td className="border p-1 rs:p-2 text-[11px] sm:text-sm">
                                                {item.monthlyCreated
                                                    ? new Date(item.monthlyCreated).toLocaleString("default", {
                                                        month: "long",
                                                        year: "numeric",
                                                    })
                                                    : "-"}
                                            </td>

                                            {/* 
                                            <td className="border p-1 rs:p-2 text-[11px] sm:text-sm">
                                                ₹ {item.annualCtc}
                                            </td> */}

                                            <td className="border p-1 rs:p-2 text-[11px] sm:text-sm hidden custom:table-cell">
                                                Total: ₹ {formatAmount(item.earnings?.totalEarnings)}
                                            </td>

                                            <td className="border p-1 rs:p-2 text-[11px] sm:text-sm hidden custom:table-cell">
                                                Total: ₹ {formatAmount(item.deductions?.totalDeductions)}
                                            </td>

                                            <td className="hidden custom:table-cell border p-1 rs:p-2 text-[11px] sm:text-sm font-semibold text-green-700">
                                                ₹ {formatAmount(item.netSalary)}
                                            </td>

                                            <td className="border p-1 rs:p-2 text-[11px] sm:text-sm">
                                                <div className="flex items-center justify-center gap-5">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <span
                                                            className={`font-medium ${statusColor[item.status] || "text-gray-600"}`}
                                                        >
                                                            {item.status || "Pending"}
                                                        </span>

                                                        <div className="p-1 rounded cursor-pointer hover:bg-blue-100">
                                                            {item.status !== 'Paid' && (<Pencil
                                                                size={16}
                                                                className="cursor-pointer text-blue-500"
                                                                onClick={() => {
                                                                    if (openSelectIndex === idx) {
                                                                        setOpenSelectIndex(null);
                                                                    } else {
                                                                        setOpenSelectIndex(idx);
                                                                        setTempStatus(item.status || "Pending");
                                                                    }
                                                                }}
                                                            />)}
                                                        </div>
                                                        {loadingRowIndex === actualIndex && (
                                                            <span className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></span>
                                                        )}
                                                    </div>

                                                    {openSelectIndex === idx && (
                                                        <select
                                                            ref={selectRef}
                                                            value={tempStatus}
                                                            onChange={(e) => {
                                                                const newStatus = e.target.value;
                                                                setTempStatus(newStatus);
                                                                handleStatusChange(actualIndex, newStatus);
                                                            }}
                                                            className={`border rounded px-2 py-1 ${statusColor[tempStatus] || "text-gray-600"}`}
                                                        >
                                                            {statusSalery.map((status) => (
                                                                <option key={status} value={status} className={statusColor[status]}>
                                                                    {status}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    )}
                                                </div>
                                            </td>

                                            <td className="border p-1 rs:p-2 text-[11px] sm:text-sm">
                                                {item.sendCreated
                                                    ? new Date(item.sendCreated).toLocaleDateString()
                                                    : "Not Sent"}
                                            </td>

                                            <td className=" p-1 rs:p-2 text-[11px] sm:text-sm border">
                                                <div className="flex justify-center gap-2">
                                                    <div className="p-1 rounded cursor-pointer hover:bg-blue-100" onClick={() => handleEdit(item, actualIndex)}>
                                                        <Pencil
                                                            size={16}
                                                            color="blue"
                                                            className="cursor-pointer"
                                                        />
                                                    </div>
                                                    <div className="p-1 rounded cursor-pointer hover:bg-orange-100" onClick={() => setPreviewData(item)}>
                                                        <Eye
                                                            size={16}
                                                            color="orange"
                                                            className="cursor-pointer"
                                                        />
                                                    </div>
                                                </div>
                                            </td>

                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {previewData && (
                    <div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-2 sm:px-4"
                        onClick={() => setPreviewData(null)}
                    >
                        <div
                            className="bg-white relative rounded-lg w-full max-w-3xl shadow-lg h-[80vh] animate-scaleIn overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="sticky top-0 z-10 flex justify-between items-center bg-gray-900 px-2 sm:px-4 py-2">
                                <h2 className="text-white text-[12px] sm:text-[16px]">Salary Details</h2>
                                <button
                                    onClick={() => setPreviewData(null)}
                                    className="text-white hover:text-red-400"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="p-2 sm:p-6 overflow-y-auto h-[calc(80vh-48px)]">
                                <table className="w-full border border-gray-200 rounded-md mb-6">
                                    <tbody className="divide-y divide-gray-200">
                                        <InfoRow
                                            label="Date"
                                            value={new Date(previewData.createdAt).toLocaleDateString()}
                                        />
                                        <InfoRow
                                            label="Status"
                                            value={
                                                <span
                                                    className={`ml-0 px-2 py-1 text-xs rounded ${statusColor[previewData.status] || "bg-gray-100 text-gray-700"
                                                        }`}
                                                >
                                                    {previewData.status || "Pending"}
                                                </span>
                                            }
                                        />
                                        <InfoRow
                                            label="Annual CTC"
                                            value={`₹ ${formatAmount(previewData.annualCtc)}`}
                                        />
                                        <InfoRow
                                            label="Monthly Salary"
                                            value={`₹ ${formatAmount(previewData.earnings?.basicSalary)}`}
                                        />
                                        <InfoRow
                                            label="Night Shift Allowance"
                                            value={`₹ ${formatAmount(previewData.earnings?.nightShiftAllowance)}`}
                                        />
                                        <InfoRow
                                            label="Performance Bonus"
                                            value={`₹ ${formatAmount(previewData.earnings?.performanceBonus)}`}
                                        />
                                        <InfoRow
                                            label="Increment Amount"
                                            value={`₹ ${formatAmount(previewData.earnings?.incrementAmount)}`}
                                        />
                                        <InfoRow
                                            label="Total Earnings"
                                            value={`₹ ${formatAmount(previewData.earnings?.totalEarnings)}`}
                                        />

                                        <InfoRow
                                            label="Loan Advance"
                                            value={`₹ ${formatAmount(previewData.deductions?.loanAdvance)}`}
                                        />
                                        <InfoRow
                                            label="Leave Deduction"
                                            value={`₹ ${formatAmount(previewData.deductions?.leaveDeduction)}`}
                                        />
                                        <InfoRow
                                            label="Other Deductions"
                                            value={`₹ ${formatAmount(previewData.deductions?.otherDeduction)}`}
                                        />
                                        <InfoRow
                                            label="Total Deductions"
                                            value={`₹ ${formatAmount(previewData.deductions?.totalDeductions)}`}
                                        />

                                        <tr className="text-[11px] sm:text-sm font-bold">
                                            <td className="px-4 py-2">Net Salary</td>
                                            <td className="px-4 py-2 text-right text-green-700">
                                                ₹ {formatAmount(previewData.netSalary)}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    handlePageChange={handlePageChange}
                />
            </div>
            <div className="mt-4 sm:mt-8 ">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white
                    rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 transform">
                    <div className="flex items-center justify-between">
                        <div className="flex gap-2 items-center">
                            <HandCoins className="w-6 h-6 md:w-8 md:h-8 xl:w-12 xl:h-12 opacity-80" />
                            <p className="text-[12px] sm:text-sm xl:text-base font-medium opacity-80">Total Amount</p>
                        </div>
                        <p className="text-[14px] sm:text-lg xl:text-3xl font-bold">
                            ₹{totalSalary}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Row({ label, value, onChange, disabled = false }) {
    return (
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-2">

            <div className="flex-1 text-[11px] sm:text-sm ">{label}</div>

            <div className="w-60">
                <Input
                    type="number"
                    value={value}
                    disabled={disabled}
                    className={disabled ? "bg-gray-100 cursor-not-allowed" : ""}
                    onChange={(e) => onChange(e.target.value)}
                />
            </div>

        </div>
    );
}
